import { z } from "zod";
export const ErrorResponse = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
    }),
});
export const HealthResponse = z.object({
    status: z.enum(["ok", "degraded"]),
    database: z.enum(["up", "down"]),
});
/**
 * Named permissions, not role strings. Endpoints and services ask for a
 * permission; which roles hold it is a row in the database, so adding a role is
 * a migration rather than an edit to every call site (docs/DECISIONS/0006).
 */
export const PERMISSIONS = [
    "site:read",
    "settings:read",
    "settings:write",
    "sessions:read:self",
    "sessions:revoke:self",
    "audit:read",
    "users:read",
    "tariffs:read",
    "tariffs:write",
];
export const Permission = z.enum(PERMISSIONS);
export const LoginRequest = z
    .object({
    email: z.string().email().max(254),
    password: z.string().min(1).max(1024),
})
    .strict();
export const TokenPair = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int().positive(),
    refreshToken: z.string(),
    refreshExpiresIn: z.number().int().positive(),
});
/** Kept as the login response name so the client reads naturally. */
export const LoginResponse = TokenPair;
export const RefreshRequest = z.object({ refreshToken: z.string().min(1).max(512) }).strict();
export const LogoutRequest = RefreshRequest;
export const CurrentUser = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    role: z.string(),
    permissions: z.array(Permission),
});
/**
 * One entry per sign-in, not per token. Refresh rotates the token on every use,
 * so the raw rows grow all day; what a person means by "a session" is the
 * family those rows belong to, and revoking it revokes the family.
 */
export const Session = z.object({
    id: z.string().uuid(),
    signedInAt: z.string().datetime(),
    lastUsedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    userAgent: z.string().nullable(),
    ip: z.string().nullable(),
    current: z.boolean(),
});
export const SessionList = z.object({ sessions: z.array(Session) });
export const AuditEntry = z.object({
    id: z.string(),
    at: z.string().datetime(),
    actorEmail: z.string().nullable(),
    action: z.string(),
    detail: z.record(z.unknown()),
    ip: z.string().nullable(),
});
export const AuditQuery = z
    .object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    // A cursor, echoed back from a previous page's `nextBefore`. Constrained to
    // digits because it is cast to bigint in SQL, where anything else is an
    // error the caller would see as a 500 rather than a 400.
    before: z
        .string()
        .regex(/^\d{1,19}$/)
        .optional(),
})
    .strict();
export const AuditPage = z.object({
    entries: z.array(AuditEntry),
    nextBefore: z.string().nullable(),
});
/**
 * What the collector puts on the bus for every framed, CRC-checked record.
 * The collector and the ingest worker are separate processes on separate
 * networks, so this is a contract between them and not an internal shape.
 */
export const FrameMessage = z
    .object({
    /** Stable across an MQTT redelivery, which is what makes ingestion idempotent. */
    frameId: z.string().uuid(),
    receivedAt: z.string().datetime(),
    protocol: z.number().int().min(0).max(65535),
    recordType: z.number().int().min(0).max(255),
    /** The record verbatim, base64. Bounded by the collector's own frame limit. */
    payload: z.string().min(1).max(8192),
})
    .strict();
/** The collector's counters, published on its own topic and stored as-is. */
export const CollectorStatus = z
    .object({
    at: z.string().datetime(),
    connectionsAccepted: z.number().int(),
    connectionsRejected: z.number().int(),
    connectionsOpen: z.number().int(),
    upstreamFailures: z.number().int(),
    bytesToGrowatt: z.number().int(),
    bytesToInverter: z.number().int(),
    recordsFramed: z.number().int(),
    recordsPublished: z.number().int(),
    crcFailures: z.number().int(),
    commandsBlocked: z.number().int(),
    desynchronisations: z.number().int(),
    lastFrameAt: z.string().datetime().nullable(),
})
    .strict();
/**
 * Instance settings. Every entry needs a schema and a default so a fresh
 * install with an empty table boots and is usable (docs/REQUIREMENTS.md
 * section 2). Adding one here also requires a UI field and a README line.
 *
 * It lives here rather than in `@spf/settings` because the settings editor
 * sends and receives it over HTTP, and section 3 forbids a hand-duplicated
 * type on either side of a request. The dependency runs one way only:
 * `@spf/settings` imports this. The reverse would put `pg` in the browser
 * bundle, since `apps/web` imports this package.
 */
export const InstanceSettings = z.object({
    siteName: z.string().min(1).max(120).default("My Solar Site"),
    timezone: z.string().min(1).max(64).default("Africa/Lagos"),
    currency: z.string().length(3).default("NGN"),
    /**
     * How old the newest reading may be before every telemetry view marks itself
     * stale (docs/REQUIREMENTS.md section 4). Three missed reports at this
     * hardware's five-minute cadence, so one late frame is not an alarm and a
     * dongle that has stopped is visible within a quarter of an hour.
     */
    dataStaleSeconds: z.coerce.number().int().min(60).max(86_400).default(900),
    /**
     * Usable battery capacity. The inverter does not report it -- it reports
     * state of charge as a percentage and nothing that percentage is a
     * percentage of -- so autonomy and time-to-full cannot be derived without
     * an operator telling us this number.
     *
     * The default is this installation's own bank rather than a neutral value,
     * because a wrong capacity produces a confident wrong answer about how long
     * the house can run, which is worse than no answer.
     */
    batteryCapacityKwh: z.coerce.number().min(0.1).max(200).default(15),
    /**
     * How far down the bank may be taken before it counts as empty.
     *
     * Runtime computed to a truly flat battery overstates what is available,
     * and it does so at the exact moment somebody is relying on it -- during an
     * outage. The floor is configurable because it is a property of the
     * chemistry, not of the inverter: 10 suits the LiFePO4 bank installed here,
     * where lead-acid would want 20 or more.
     */
    batteryReserveSocPct: z.coerce.number().min(0).max(90).default(10),
});
/** The settings as served, with when any of them last changed. */
export const InstanceSettingsResponse = z.object({
    values: InstanceSettings,
    updatedAt: z.string().datetime(),
});
/**
 * A full replacement rather than a patch. Every key has a default, so a whole
 * object is always expressible, and it makes the audit diff exact: what
 * changed is old against new, with nothing inferred from absence.
 */
export const InstanceSettingsRequest = InstanceSettings;
/**
 * The newest stored reading. Solar is served from slice 5c, its fields verified
 * against Growatt's own decode and the inverter's counters on 2026-09-30
 * (docs/DECISIONS/0028).
 */
export const Reading = z.object({
    time: z.string().datetime(),
    /** Both strings together. Zero at night is a measurement, not a gap. */
    pvPowerW: z.number(),
    loadPowerW: z.number(),
    batteryVoltageV: z.number(),
    /** Negative is charging, the sign the inverter reports for battery power. */
    batteryCurrentA: z.number().nullable(),
    batteryPowerW: z.number(),
    socPct: z.number().int(),
    gridVoltageV: z.number(),
    gridPowerW: z.number(),
    inverterTempC: z.number(),
    /** True for a record the dongle stored during an outage and replayed later. */
    isBuffered: z.boolean(),
    fieldMapVersion: z.number().int(),
    // Nullable throughout, and null means "not recorded" rather than zero. Two
    // separate reasons a client will see one: the row predates the column, or it
    // is older than the 90-day raw_frames retention and so can never be
    // backfilled. A view that renders these as 0 would invent a measurement.
    gridFrequencyHz: z.number().nullable(),
    outputVoltageV: z.number().nullable(),
    outputFrequencyHz: z.number().nullable(),
    loadPercent: z.number().nullable(),
    dcdcTempC: z.number().nullable(),
    fanSpeedPct: z.number().nullable(),
    // The inverter's own cumulative counters, kept so a derived energy series can
    // be reconciled against the hardware's own arithmetic rather than trusted.
    energyChargedTodayKwh: z.number().nullable(),
    energyChargedTotalKwh: z.number().nullable(),
    batteryDischargedTodayKwh: z.number().nullable(),
    batteryDischargedTotalKwh: z.number().nullable(),
    acOutputTodayKwh: z.number().nullable(),
    acOutputTotalKwh: z.number().nullable(),
    // Nullable for the same reasons as the fields above: a row older than the
    // columns, or than the raw_frames retention, has no value to serve.
    /** The first string's voltage; the second input has nothing connected. */
    pvVoltageV: z.number().nullable(),
    pvEnergyTodayKwh: z.number().nullable(),
    pvEnergyTotalKwh: z.number().nullable(),
});
/**
 * Freshness is decided by the server, not by the browser. A view that worked
 * it out from a timestamp would put a business rule in the client, and a
 * second client would have to reimplement it (docs/REQUIREMENTS.md section 3).
 */
/**
 * What the battery's charge means in hours, worked out on the server.
 *
 * A browser could do this arithmetic, but then the rule about how empty
 * "empty" is would live in the client and the next client would have to
 * reimplement it (docs/REQUIREMENTS.md section 3). It also depends on two
 * settings the browser has no reason to hold.
 *
 * `usableKwh` and both durations stop at `reserveSocPct`, not at zero. Runtime
 * to a flat battery overstates what is there, at the moment somebody is
 * relying on it.
 */
export const BatteryOutlook = z.object({
    capacityKwh: z.number(),
    reserveSocPct: z.number(),
    /** Energy above the reserve floor. Zero at or below it, never negative. */
    usableKwh: z.number(),
    /**
     * Hours the present load can be carried on what is above the floor. Null
     * when nothing is drawing, because the answer would be unbounded rather
     * than large.
     */
    autonomyHours: z.number().nullable(),
    /** Hours to full at the present charge rate. Null when not charging. */
    timeToFullHours: z.number().nullable(),
});
export const LatestReading = z.object({
    reading: Reading.nullable(),
    ageSeconds: z.number().nullable(),
    stale: z.boolean(),
    staleAfterSeconds: z.number().int(),
    /** Null when there is no reading to derive it from. */
    battery: BatteryOutlook.nullable(),
});
/**
 * The ingestion liveness signal of docs/REQUIREMENTS.md section 10: how long
 * since a frame arrived, and how many failed to decode.
 */
export const IngestStatus = z.object({
    lastFrameAt: z.string().datetime().nullable(),
    lastReadingAt: z.string().datetime().nullable(),
    framesIngested: z.number().int(),
    /** Keepalives and configuration exchanges: framed and forwarded, nothing to store. */
    framesIgnored: z.number().int(),
    /**
     * Data records stored with no reading, because the inverter answered with no
     * value where every reading needs one (docs/DECISIONS/0031).
     */
    framesNoReading: z.number().int(),
    framesFailed: z.number().int(),
    framesRejected: z.number().int(),
    collector: CollectorStatus.nullable(),
});
/**
 * The Postgres channel a written reading announces itself on, and what the
 * notification carries. The ingest worker writes and the API listens; they are
 * separate processes, so this is a contract between them rather than an
 * internal shape, for the same reason FrameMessage is.
 *
 * Only the instant travels. The API re-reads the reading itself, so a
 * notification can never be a second, staler source of truth for the values,
 * and the payload cannot approach Postgres' 8000-byte limit.
 */
export const READING_CHANNEL = "spf_reading";
export const ReadingNotice = z.object({ time: z.string().datetime() }).strict();
/** The `event:` name every frame on the telemetry stream carries. */
export const TELEMETRY_EVENT = "telemetry";
/**
 * The last event on a stream the server is closing, and why. The client
 * reconnects regardless -- the reason is what makes a closed stream legible in
 * a log instead of looking like a dropped connection.
 */
export const TELEMETRY_END_EVENT = "end";
export const StreamEnd = z
    .object({ reason: z.enum(["token_expired", "session_revoked", "server_closing"]) })
    .strict();
export const SiteInfo = z.object({
    siteName: z.string(),
    timezone: z.string(),
    currency: z.string(),
    updatedAt: z.string().datetime(),
});
export const API_PREFIX = "/api/v1";
export * from "./energy.js";
export * from "./tariffs.js";
export * from "./outages.js";
//# sourceMappingURL=index.js.map